import React, { useState, useEffect, useRef, useCallback } from "react";
import { computeSnap } from "../utils/snapAlign";
import { usePointerDrag } from "../hooks/usePointerDrag";
import { wasJustPlaced } from "../utils/motion";
import { useUndoGesture } from "../hooks/useUndoGesture";

const MIN_FONT_SIZE = 24;
const MAX_FONT_SIZE = 120;
const MIN_SCALE = 0.3;
const MAX_SCALE = 4;

const handleBaseStyle = {
  position: "absolute",
  zIndex: 14,
  background: "#fffdf6",
  border: "1px solid #00a4e4",
  boxSizing: "border-box",
  touchAction: "none",
};

const SFXText = ({ sfx, comicStyle, cellSize, onChange, onSelect, isSelected, siblingRects, snapGridSize, snapEnabled = true, onGuidesChange, onDuplicate }) => {
  const styleSFX = comicStyle?.sfx || {};
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draftText, setDraftText] = useState(sfx.text || "BOOM");
  const [placing] = useState(() => wasJustPlaced(sfx.id));
  const textRef = useRef(null);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });

  const text = sfx.text || "BOOM";
  const fontSize = sfx.fontSize || 64;
  const fontFamily = sfx.fontFamily || styleSFX.fontFamily || "'Bangers', cursive";
  const color = sfx.color || styleSFX.color || "#ff0000";
  const strokeColor = sfx.strokeColor || styleSFX.strokeColor || "#000000";
  const rotation = sfx.rotation || 0;
  const x = sfx.x || 20;
  const y = sfx.y || 20;
  const scaleX = sfx.scaleX ?? 1;
  const scaleY = sfx.scaleY ?? 1;

  useEffect(() => {
    setDraftText(sfx.text || "BOOM");
  }, [sfx.text]);

  // Measure natural text dimensions (unscaled). Re-measure whenever the
  // text's layout size changes — notably when the web font finishes loading
  // and replaces the fallback, which would otherwise leave the box (and its
  // rotation centre, outline, and handles) sized for the wrong font.
  useEffect(() => {
    const el = textRef.current;
    if (!el) return undefined;
    const measure = () => {
      setNaturalSize((prev) => (prev.width === el.offsetWidth && prev.height === el.offsetHeight
        ? prev
        : { width: el.offsetWidth, height: el.offsetHeight }));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, fontSize, fontFamily, isEditing]);

  // Scaled display dimensions
  const displayWidth = naturalSize.width * scaleX;
  const displayHeight = naturalSize.height * scaleY;

  // ─── Pointer drags: move the SFX or stretch it ──────────────────────────
  const handleBodyMove = ({ dx, dy, event, payload }) => {
    const rawX = Math.max(0, Math.min(cellSize.width - 40, payload.origX + dx));
    const rawY = Math.max(0, Math.min(cellSize.height - 20, payload.origY + dy));

    const { snappedRect, guides } = computeSnap({
      rect: { x: rawX, y: rawY, width: displayWidth, height: displayHeight },
      cellSize,
      siblings: siblingRects || [],
      gridSize: snapGridSize || 10,
      skipSnap: event.altKey || !snapEnabled,
    });

    onChange({ x: snappedRect.x, y: snappedRect.y });
    onGuidesChange && onGuidesChange(guides);
  };

  // Edges/corners adjust scaleX/scaleY
  const handleResizeMove = ({ dx, dy, event, payload: resizing }) => {
    const edge = resizing.edge;
    const nw = resizing.naturalWidth;
    const nh = resizing.naturalHeight;
    let nextScaleX = resizing.origScaleX;
    let nextScaleY = resizing.origScaleY;

    if (edge === "right" || edge === "se" || edge === "ne") {
      nextScaleX = Math.max(MIN_SCALE, Math.min(MAX_SCALE, resizing.origScaleX + dx / nw));
    }
    if (edge === "left" || edge === "sw" || edge === "nw") {
      nextScaleX = Math.max(MIN_SCALE, Math.min(MAX_SCALE, resizing.origScaleX - dx / nw));
    }
    if (edge === "bottom" || edge === "se" || edge === "sw") {
      nextScaleY = Math.max(MIN_SCALE, Math.min(MAX_SCALE, resizing.origScaleY + dy / nh));
    }
    if (edge === "top" || edge === "ne" || edge === "nw") {
      nextScaleY = Math.max(MIN_SCALE, Math.min(MAX_SCALE, resizing.origScaleY - dy / nh));
    }

    // Shift key: proportional scaling
    if (event.shiftKey && (edge === "se" || edge === "ne" || edge === "sw" || edge === "nw")) {
      const avgScale = (nextScaleX + nextScaleY) / 2;
      nextScaleX = avgScale;
      nextScaleY = avgScale;
    }

    onChange({
      scaleX: Math.round(nextScaleX * 100) / 100,
      scaleY: Math.round(nextScaleY * 100) / 100,
    });
  };

  const drag = usePointerDrag({
    onMove: (move) => (move.payload.kind === "resize" ? handleResizeMove(move) : handleBodyMove(move)),
    onEnd: () => onGuidesChange && onGuidesChange([]),
  });
  const dragKind = drag.active?.kind || null;
  useUndoGesture(Boolean(dragKind));

  const startResize = useCallback((edge, event) => {
    if (sfx.locked) return;
    event.preventDefault();
    event.stopPropagation();
    drag.start(event, {
      kind: "resize",
      edge,
      origScaleX: scaleX,
      origScaleY: scaleY,
      naturalWidth: naturalSize.width || 100,
      naturalHeight: naturalSize.height || 40,
    });
  }, [sfx.locked, scaleX, scaleY, naturalSize, drag.start]);

  const handleTextCommit = () => {
    setIsEditing(false);
    onChange({ text: draftText });
  };

  const showControls = isHovered || isSelected || dragKind === "resize";

  // Padding inside the outer div
  const pad = 4;

  return (
    <div
      data-canvas-item="sfx"
      data-item-id={sfx.id}
      style={{
        position: "absolute",
        left: x,
        top: y,
        width: displayWidth + pad * 2,
        height: displayHeight + pad * 2,
        transform: `rotate(${rotation}deg)`,
        transformOrigin: "center center",
        cursor: sfx.locked ? "default" : isEditing ? "text" : dragKind === "move" ? "grabbing" : "grab",
        touchAction: "none",
        zIndex: 11,
        pointerEvents: "auto",
        outline: isSelected ? "2px dashed #00a4e4" : undefined,
        outlineOffset: isSelected ? 2 : undefined,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onPointerDown={(e) => {
        if (isEditing) return;
        e.preventDefault();
        e.stopPropagation();
        // Alt+click = duplicate
        if (e.altKey && onDuplicate) {
          onDuplicate();
          return;
        }
        onSelect && onSelect();
        if (sfx.locked) return;
        drag.start(e, { kind: "move", origX: x, origY: y });
      }}
      onClick={(e) => {
        e.stopPropagation();
        onSelect && onSelect();
      }}
    >
      {/* Lift/place motion lives on this inner layer: the outer one carries
          the SFX's own rotation transform */}
      <div
        className={`striply-lift${dragKind === "move" && drag.moving ? " is-lifted" : ""}${placing ? " striply-place" : ""}`}
        style={{ padding: pad, overflow: "visible" }}
      >
        {isEditing ? (
          <input
            autoFocus
            value={draftText}
            onChange={(e) => setDraftText(e.target.value)}
            onBlur={handleTextCommit}
            onKeyDown={(e) => { if (e.key === "Enter") handleTextCommit(); }}
            style={{
              fontSize,
              fontFamily,
              color,
              background: "rgba(255,253,246,0.85)",
              border: "1px dashed #00a4e4",
              borderRadius: 0,
              outline: "none",
              padding: "2px 4px",
              width: Math.max(80, text.length * fontSize * 0.6),
            }}
          />
        ) : (
          <span
            ref={textRef}
            style={{
              fontSize,
              fontFamily,
              fontWeight: "bold",
              color,
              WebkitTextStroke: `2px ${strokeColor}`,
              textShadow: `2px 2px 0 ${strokeColor}`,
              whiteSpace: "nowrap",
              userSelect: "none",
              letterSpacing: 2,
              display: "inline-block",
              transform: `scale(${scaleX}, ${scaleY})`,
              transformOrigin: "top left",
            }}
            onDoubleClick={() => setIsEditing(true)}
          >
            {text}
          </span>
        )}
      </div>

      {/* Rotation buttons */}
      {showControls && !sfx.locked && (
        <div
          style={{
            position: "absolute",
            top: -24,
            right: -8,
            display: "flex",
            gap: 4,
          }}
        >
          <button
            type="button"
            title="Rotate -15°"
            onPointerDown={(e) => { e.stopPropagation(); }}
            onClick={(e) => {
              e.stopPropagation();
              onChange({ rotation: (rotation - 15) % 360 });
            }}
            style={{
              width: 20, height: 20, fontSize: 10, padding: 0,
              border: "1px solid #1a1a1a", background: "#fffdf6", color: "#1a1a1a", cursor: "pointer",
            }}
          >↶</button>
          <button
            type="button"
            title="Rotate +15°"
            onPointerDown={(e) => { e.stopPropagation(); }}
            onClick={(e) => {
              e.stopPropagation();
              onChange({ rotation: (rotation + 15) % 360 });
            }}
            style={{
              width: 20, height: 20, fontSize: 10, padding: 0,
              border: "1px solid #1a1a1a", background: "#fffdf6", color: "#1a1a1a", cursor: "pointer",
            }}
          >↷</button>
        </div>
      )}

      {/* Resize handles */}
      {showControls && !isEditing && !sfx.locked && (
        <>
          {/* Edge handles */}
          <div style={{ ...handleBaseStyle, top: -3, left: 8, right: 8, height: 6, cursor: "ns-resize" }}
            onPointerDown={(e) => startResize("top", e)} />
          <div style={{ ...handleBaseStyle, bottom: -3, left: 8, right: 8, height: 6, cursor: "ns-resize" }}
            onPointerDown={(e) => startResize("bottom", e)} />
          <div style={{ ...handleBaseStyle, left: -3, top: 8, bottom: 8, width: 6, cursor: "ew-resize" }}
            onPointerDown={(e) => startResize("left", e)} />
          <div style={{ ...handleBaseStyle, right: -3, top: 8, bottom: 8, width: 6, cursor: "ew-resize" }}
            onPointerDown={(e) => startResize("right", e)} />
          {/* Corner handles */}
          <div style={{ ...handleBaseStyle, top: -4, left: -4, width: 10, height: 10, cursor: "nwse-resize" }}
            onPointerDown={(e) => startResize("nw", e)} />
          <div style={{ ...handleBaseStyle, top: -4, right: -4, width: 10, height: 10, cursor: "nesw-resize" }}
            onPointerDown={(e) => startResize("ne", e)} />
          <div style={{ ...handleBaseStyle, bottom: -4, left: -4, width: 10, height: 10, cursor: "nesw-resize" }}
            onPointerDown={(e) => startResize("sw", e)} />
          <div style={{ ...handleBaseStyle, bottom: -4, right: -4, width: 10, height: 10, cursor: "nwse-resize" }}
            onPointerDown={(e) => startResize("se", e)} />
        </>
      )}
    </div>
  );
};

export default SFXText;
